import React, { Fragment, useState } from 'react';
import { Col, Form, Input, Row, Select, Switch, Tag } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';

import FormListRemoveButton from '../../FormListRemoveButton';
import { DELIVERY_SERVICES } from '../../../../../shared/enums/DeliveryServices';
import { DateFee, DistanceFee, PriceMatrixAttributes, ServiceFee } from '../../../../../server/src/models';
import DateFeeInput from './DateFeeInput';
import DistanceFeeInput from './DistanceFeeInput';
import FormListAddButton from '../../FormListAddButton';
import { ClientFeatureConfig } from '../../../types/Feature';

const { Option, OptGroup } = Select;

const firstTitleStyle = { fontSize: '18px' };
const secondTitleStyle = { fontSize: '14px' };

interface ServiceFeesListProps {
    isDetailMode: boolean;
    serviceFees?: ServiceFee[];
}

interface ServiceFeeInputProps extends ServiceFeesListProps {
    removeItem: (index: number | number[]) => void;
    item: FormListFieldData;
}

const serviceOptions: Record<string, { value: DELIVERY_SERVICES; label: string }[]> = {
    Delivery: [
        {
            value: DELIVERY_SERVICES.SERVICE_ASSEMBLY,
            label: 'Assembly',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_INSTALL,
            label: 'Install',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_SIMPLE_INSTALL,
            label: 'Install (simple)',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_COMPLEX_INSTALL,
            label: 'Install (complex)',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_RECOVERY,
            label: 'Recovery',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_RETURN,
            label: 'Return',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_UNPACKING,
            label: 'Unpacking',
        },
    ],
    Transport: [
        {
            value: DELIVERY_SERVICES.SERVICE_GREEN,
            label: 'Green',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_STANDARD_TAXI,
            label: 'Taxi (standard)',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_XL_TAXI,
            label: 'Taxi (XL)',
        },
    ],
    Other: [
        {
            value: DELIVERY_SERVICES.SERVICE_PAY_ON_DELIVERY,
            label: 'Pay on delivery',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_ECOMMERCE,
            label: 'Web',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_WITH_SIGNATURE,
            label: 'With signature',
        },
    ],
    Location: [
        {
            value: DELIVERY_SERVICES.SERVICE_FRONT_HOME,
            label: 'Front home',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_REGULAR_FRONT_HOME,
            label: 'Front home (regular)',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_PICKUP_POINT,
            label: 'Pickup point',
        },
        {
            value: DELIVERY_SERVICES.SERVICE_SELECTED_ROOM,
            label: 'Selected room',
        },
    ],
};

const ServiceFeeInput = ({ isDetailMode, serviceFees, item, removeItem }: ServiceFeeInputProps) => {
    const [isPerPackage, setIsPerPackage] = useState(serviceFees?.[item.key]?.forWholeDelivery);

    return (
        <Fragment key={item.key}>
            <Row gutter={24}>
                <Col span={7}>
                    <Form.Item
                        labelCol={{ span: 12 }}
                        name={[item.name, 'code']}
                        label={isDetailMode ? '' : 'Service'}
                        style={{ fontWeight: 'bold' }}
                        rules={[
                            {
                                required: !isDetailMode,
                                message: 'Service is required',
                            },
                        ]}
                    >
                        {isDetailMode ? (
                            <span>
                                {Object.values(serviceOptions)
                                    .flat()
                                    .find((so) => so.value === serviceFees?.[item.key]?.code)?.label ||
                                    serviceFees?.[item.key]?.code}
                            </span>
                        ) : (
                            <Select showArrow>
                                {Object.keys(serviceOptions).map((k) => (
                                    <OptGroup key={k} label={k}>
                                        {serviceOptions[k].map(({ label, value }) => (
                                            <Option key={value} value={value}>
                                                {label}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                </Col>
                <Col span={7}>
                    <Form.Item
                        labelCol={{ span: 12 }}
                        name={[item.name, 'forWholeDelivery']}
                        valuePropName="checked"
                        className="m-0"
                        style={{ fontWeight: 'bold' }}
                        label="Per package"
                    >
                        {isDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                <Tag color={isPerPackage ? 'default' : 'blue'}>
                                    {isPerPackage ? 'Per delivery' : 'Per package'}
                                </Tag>
                            </span>
                        ) : (
                            <Switch
                                style={{ backgroundColor: `${isPerPackage ? 'rgba(0, 0, 0, 0.25)' : '#1890ff'}` }}
                                title={isPerPackage ? 'Per delivery' : 'Per package'}
                                unCheckedChildren="Per package"
                                checkedChildren="Per delivery"
                                onChange={() => setIsPerPackage(!isPerPackage)}
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col span={7}>
                    <Form.Item
                        labelCol={{ span: 12 }}
                        name={[item.name, 'price']}
                        label={isDetailMode ? '' : 'Price'}
                        style={{ fontWeight: 'bold' }}
                        rules={[
                            {
                                required: !isDetailMode,
                                message: 'Price is required',
                            },
                        ]}
                    >
                        {isDetailMode ? (
                            <span>{serviceFees?.[item.key]?.price}</span>
                        ) : (
                            <Input type="number" title="Service's price" />
                        )}
                    </Form.Item>
                </Col>
                <Col span={1} offset={1}>
                    <FormListRemoveButton isDetailMode={isDetailMode} remove={removeItem} name={item.name} />
                </Col>
            </Row>
        </Fragment>
    );
};

const ServiceFeesList = ({ isDetailMode, serviceFees }: ServiceFeesListProps) => (
    <>
        <div
            className="section-service-fees d-flex justify-content-between align-items-center bg-white"
            style={{ padding: '.75rem 1.25rem' }}
        >
            <p className="m-0 p-0 h5" style={secondTitleStyle}>
                Service Fees
            </p>
        </div>
        <div className="card-body">
            <Row>
                <Col span={23} offset={1}>
                    <Form.List name="serviceFees">
                        {(items, { add: newItem, remove: removeItem }): JSX.Element => (
                            <>
                                {items.map((item) => (
                                    <ServiceFeeInput
                                        key={item.key}
                                        item={item}
                                        removeItem={removeItem}
                                        serviceFees={serviceFees}
                                        isDetailMode={isDetailMode}
                                    />
                                ))}
                                <FormListAddButton
                                    id="add-service-price"
                                    add={newItem}
                                    isDetailMode={isDetailMode}
                                    text="Add a service fee"
                                />
                            </>
                        )}
                    </Form.List>
                </Col>
            </Row>
        </div>
    </>
);

const DistanceFeesList = ({
    isDetailMode,
    distanceFees,
}: {
    isDetailMode: boolean;
    distanceFees: DistanceFee[] | undefined;
}) => {
    return (
        <>
            <div
                className="section-distance-fees d-flex justify-content-between align-items-center bg-white"
                style={{ padding: '.75rem 1.25rem' }}
            >
                <p className="m-0 p-0 h5" style={secondTitleStyle}>
                    Distance Fees
                </p>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={23} offset={1}>
                        <Form.List name="distanceFees">
                            {(fees, { add: newFee, remove: removeFee }): JSX.Element => (
                                <>
                                    {fees.map((fee) => (
                                        <DistanceFeeInput
                                            key={fee.key}
                                            isDetailMode={isDetailMode}
                                            distanceFee={distanceFees?.[fee.key]}
                                            fieldName={fee.name}
                                            remove={removeFee}
                                        />
                                    ))}
                                    <FormListAddButton
                                        id="add-price-matrix-distance-fees"
                                        add={newFee}
                                        isDetailMode={isDetailMode}
                                        text="Add a distance fee"
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </div>
        </>
    );
};

const DateFeesList = ({ isDetailMode, dateFees }: { isDetailMode: boolean; dateFees: DateFee[] | undefined }) => {
    return (
        <>
            <div
                className="section-date-fees d-flex justify-content-between align-items-center bg-white"
                style={{ padding: '.75rem 1.25rem' }}
            >
                <p className="m-0 p-0 h5" style={secondTitleStyle}>
                    Date Fees
                </p>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={23} offset={1}>
                        <Form.List name="dateFees">
                            {(fees, { add: newFee, remove: removeFee }): JSX.Element => (
                                <>
                                    {fees.map((fee) => (
                                        <DateFeeInput
                                            key={fee.key}
                                            isDetailMode={isDetailMode}
                                            dateFee={dateFees?.[fee.key]}
                                            fieldName={fee.name}
                                            remove={removeFee}
                                        />
                                    ))}
                                    <FormListAddButton
                                        id="add-price-matrix-date-fees"
                                        add={newFee}
                                        isDetailMode={isDetailMode}
                                        text="Add a date fee"
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </div>
        </>
    );
};

interface FeesSectionProps {
    isDetailMode: boolean;
    priceMatrixConfig?: ClientFeatureConfig<PriceMatrixAttributes>;
}

const FeesSection = ({ isDetailMode, priceMatrixConfig }: FeesSectionProps) => {
    return (
        <>
            <div className="section-fees card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={firstTitleStyle}>
                    Fees
                </p>
            </div>
            <DistanceFeesList isDetailMode={isDetailMode} distanceFees={priceMatrixConfig?.config.distanceFees} />
            <ServiceFeesList isDetailMode={isDetailMode} serviceFees={priceMatrixConfig?.config?.serviceFees} />
            <DateFeesList isDetailMode={isDetailMode} dateFees={priceMatrixConfig?.config?.dateFees} />
        </>
    );
};

export default FeesSection;
